import {
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import FileUploader from 'components/common/file-uploader';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import {
  filterOperators,
  filterListOperators,
  dateFilterOperators,
  booleanFilterOperators,
  numberFilterOperators
} from './../../../../constants';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { equipmentService } from 'services/api';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';
import { Chip } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import equipmentStatusCell from '../cells/equipment-status-cell';
import { currencyCell } from '../cells/currency-cell';
import { booleanValueFormatter } from '../data-grid/utils';
import DataGridCell from 'components/common/DataGridCell';
import ConfirmationUnlinkTableDialog from 'components/helpers/ConfirmationGridUnlink';
import listCell from '../cells/list-cell';

const equipmentOptions = ['NEW', 'REFURBISHED', 'DECOMMISSIONED'];

export const EquipmentColumns = (
  ivdr_classes: string[],
  onUploadSuccess?: any,
  detailNav = false
): GridColDef[] => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const isSuperAdmin = IsSuperAdminGuard();

  const columns: GridColDef[] | GridColumns = [
    {
      field: 'code',
      headerName: t(`${translationPrefix}.code`),
      width: 200,
      filterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const workstation = params.row;
        return detailNav ? (
          <div>
            <Link to={`${workstation.id}`}>
              <b className="text-secondary-500">{workstation.code}</b>
            </Link>
          </div>
        ) : (
          <DataGridCell content={params.value as string} />
        );
      }
    },
    ...(isSuperAdmin
      ? [
          {
            field: 'association',
            headerName: t(`global.association`),
            width: 80,
            sortable: false,
            filterOperators: booleanFilterOperators,
            renderCell: (params: GridRenderCellParams) => (
              <div>
                <Chip
                  sx={{ background: 'transparent' }}
                  icon={params.value ? <Check color="success" /> : <Close color="error" />}
                />
              </div>
            )
          }
        ]
      : []),
    {
      field: 'description',
      filterOperators,
      width: 250,
      headerName: t(`${translationPrefix}.description`)
    },
    {
      field: 'status',
      type: 'singleSelect',
      valueOptions: equipmentOptions,
      headerName: t(`${translationPrefix}.status`),
      width: 100,
      renderCell: equipmentStatusCell(t)
    },
    {
      field: 'serial_number',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.serial_number`)
    },
    {
      field: 'internal_id',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.internal_id`)
    },
    {
      field: 'transport_document_ref',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.transport_document_ref`)
    },
    {
      field: 'supplier',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.supplier`)
    },
    {
      field: 'warehouse_code',
      filterOperators,
      width: 150,
      headerName: t(`global.warehouse`)
    },
    {
      field: 'position',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.position`)
    },
    {
      field: 'market_value',
      filterOperators: numberFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.market_value`),
      renderCell: currencyCell
    },
    {
      field: 'manufacturer',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.manufacturer`)
    },
    {
      field: 'yearly_lease_amount',
      headerName: t(`entities.contract-equipment.yearly_lease_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'yearly_maintenance_amount',
      headerName: t(`entities.contract-equipment.yearly_maintenance_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'purchase_amount',
      headerName: t(`entities.contract-equipment.purchase_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'accessories',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.accessories`)
    },
    {
      field: 'contracts_code',
      sortable: false,
      filterOperators: filterListOperators,
      width: 200,
      renderCell: listCell,
      headerName: t(`global.contracts`)
    },
    {
      field: 'delivery_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.delivery_date`)
    },
    {
      field: 'installation_date',
      width: 150,
      filterOperators: dateFilterOperators,
      headerName: t(`${translationPrefix}.installation_date`)
    },
    {
      field: 'testing_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.testing_date`)
    },
    {
      field: 'buying_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.buying_date`)
    },
    {
      field: 'decommission_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.decommission_date`)
    },
    {
      field: 'ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.ticketing_email`)
    },
    {
      field: 'cc_ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.cc_ticketing_email`)
    },
    {
      field: 'assistance_contact_number',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.assistance_contact_number`)
    }
  ];

  [
    'technical_file',
    'user_manual',
    'ce_ivd',
    'maintenance_checklist_template',
    'preemptive_maintenance_calendar'
  ].forEach((file) => {
    columns.push({
      field: file,
      headerName: t(`${translationPrefix}.${file}`),
      valueFormatter: booleanValueFormatter,
      renderCell: (params: GridRenderCellParams) => {
        const equipment = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                onUploadSuccess={onUploadSuccess}
                service={equipmentService}
                entityId={equipment.id}
                attachmentName={file}
                filename={equipment[file]}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 150,
      filterable: false,
      sortable: false
    });
  });

  columns.push({
    field: 'actions',
    type: 'actions',
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      const actions = [];
      actions.push(
        <IsTenantAdministrator key={123}>
          <ConfirmationUnlinkTableDialog
            id={params.row.id}
            handleUnlink={(id) => {
              equipmentService.purgeLinks(id).then(() => {
                if (onUploadSuccess) onUploadSuccess();
              });
            }}
          />
        </IsTenantAdministrator>
      );
      return actions;
    }
  });

  // Nascondi completamente la colonna "Associato" per non-superadmin (mai mostrarla neanche vuota)
  return (columns as GridColDef[]).filter((col) => col.field !== 'association' || isSuperAdmin);
};
